import React, {useContext, useEffect, useRef, useState} from 'react';
import LocationCard from "../../../../../components/app-specific/location-card";
import Api from "../../../../../../core/services/api_service";
import useRouter from "../../../../../hooks/use-router";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {LocationTypes} from "../../../../../../core/constants/enums";
import {ReactComponent as BackIcon} from "../../../../../../assets/images/views/location/back.svg";
import {routeFunctions} from "../../../../../routes";
import {useTranslation} from "react-i18next";
import {ReactComponent as GenerateReportIcon} from "../../../../../../assets/images/report.svg";
import BreadCrumbs from "../../../../../components/app-specific/bread-crumbs";
import NoEntries from "../../../../../components/app-specific/no-entries";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import {ReactComponent as PlusIcon} from "../../../../../../assets/images/views/location/plus.svg";
import {CompanyContext} from "../../../../../contexts/company";


const RowsView = () => {
    const {
        history, params: {
            warehouse,
            company: companyParam,
            aisle,
            section,
        }
    } = useRouter();
    const [rows, setRows] = useState([]);
    const [locationInfo, setLocationInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const isMounted = useIsMounted();
    const {t} = useTranslation();
    const translations = t('views.panel.company.location', {
        returnObjects: true,
        code: locationInfo?.section?.code ?? ''
    });

    const [company] = useContext(CompanyContext);
    const selectedCompany = useRef(company);

    /**
     * With each change in the company id:
     * - navigates user to the list of warehouses related to the selected company
     */
    useEffect(() => {
        if (selectedCompany.current?.id === company?.id)
            return;
        selectedCompany.current = company;
        history.replace(routeFunctions.panel.company.location.warehouses(company.name))
    }, [company?.id])

    /**
     * Listens to the changes of the any of the previously selected location hierarchies, and if any of them changed:
     * Fetches the list of the rows for the provided warehouse, aisle, and section
     */
    useEffect(() => {
        getLocationInfo();
    }, [warehouse, aisle, section]);

    /**
     * Gets the location address information
     */
    const getLocationInfo = () => {
        setLoading(true);
        //TODO: call the Api to load the location address information
        const response = {
            resultFlag: true,
            data: {
                warehouse: {id: warehouse, code: '00'},
                aisle: {id: aisle, code: "A5"},
                section: {id: section, code: "A5A2"}
            }
        };
        if (response?.resultFlag) {
            setLocationInfo(response?.data);
            if (window.bizpire?.makeLocationBreadcrumbs) {
                setBreadcrumbs(window.bizpire?.makeLocationBreadcrumbs(response?.data))
            }
            getRows()
        } else {
            setLoading(false)
        }
    }

    /**
     * Fetches the list of rows for the provided warehouse from the server and populates the inner state.
     */
    const getRows = () => {
        Api.getRows(section, warehouse).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setRows(response?.data ?? []);
            }
            setLoading(false);
        })
    }

    /**
     * Generates a csv report for all the parts in the selected company and the provided values as filters.
     * @param {any} values the selected filters accumulated from the dialog.
     */
    const generateReport = (values) => {
        setGeneratingReport(true);
        const forApi = {...values}
        //TODO: call api for generating report for all the parts in an aisle
        setGeneratingReport(false);
    }

    /**
     * Submits the new row to the server and appends the newly created row to the list of rows
     * @param {string} rowCode
     * @param {number} capacity
     */
    const createRow = async (rowCode, capacity) => {
        const forApi = {
            code: rowCode,
            sectionId: parseInt(section),
            capacity: capacity,
        }
        const response = await Api.createRow(forApi, parseInt(warehouse));
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setRows(prevState => [...prevState, response.data]);
        } else {
            // TODO: return errors based on the errors given in the api.
        }
    }

    /**
     * Updates the row information using the provided row id and row code
     * If the result was successful, updates the row in the list of rows
     * @param {number} rowId
     * @param {string} rowCode
     * @param {number} capacity
     */
    const updateRow = async (rowId, rowCode, capacity) => {
        const forApi = {
            id: rowId,
            code: rowCode,
            sectionId: parseInt(section),
            capacity: Number(capacity),
        }
        const response = await Api.updateRow(forApi, forApi.warehouseId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setRows(prevState => prevState?.map(e => e.row.id === rowId
                ? {...e, row: {...e.row, ...forApi}}
                : e
            ) ?? []);
        } else {
            // TODO: return errors based on the errors given in the api.
        }
    }

    /**
     * Removes the given row from the list of rows after removing it from the server.
     * @param {any} row the id associated with the row to be removed
     */
    const removeRow = (row) => {
        setRows(prevState => prevState?.map(e => e.row.id === row?.id
            ? {...e, loading: true}
            : e
        ) ?? []);
        Api.removeRow(row?.id, parseInt(warehouse)).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setRows(prevState => prevState?.filter((e) => e.row.id !== row?.id) ?? []);
            }
            setRows(prevState => prevState?.map(e => e.row.id === row?.id
                ? {...e, loading: false}
                : e
            ) ?? []);
        })
    }

    /**
     * Navigates the user to the previous location page
     */
    const navigateBack = () => {
        history.push(routeFunctions.panel.company.location.sections(companyParam, warehouse, aisle));
    }

    /**
     * Opens the general report's dialog and binds the generation report callback for successful filtering.
     */
    const showGenerateReportDialog = () => {
        const generateLocationReportDialog = window.bizpire?.generateLocationReportDialog;
        if (!generateLocationReportDialog) return;
        generateLocationReportDialog(true, locationInfo, generateReport)
    }

    /**
     * Opens the Add location dialog and binds the row creation logic to the callback of this dialog.
     */
    const showAddRowDialog = () => {
        const upsertLocationDialog = window.bizpire?.upsertLocationDialog;
        if (!upsertLocationDialog) return
        const locationCode = locationInfo?.section?.code ?? '';
        upsertLocationDialog(
            true,
            LocationTypes.row,
            undefined,
            ({code, capacity}) => createRow(code + locationCode, capacity)
        )
    }

    /**
     * Opens the Add location dialog and binds the row creation logic to the callback of this dialog.
     * @param {any} row the row to be updated.
     */
    const showEditRowDialog = (row) => {
        if (!row) return;
        const upsertLocationDialog = window.bizpire?.upsertLocationDialog;
        if (!upsertLocationDialog) return
        const locationCode = locationInfo?.section?.code ?? '';
        upsertLocationDialog(
            true,
            LocationTypes.row,
            {code: row.code?.replace(locationCode, ""), capacity: row.capacity},
            ({code, capacity}) => updateRow(row.id, locationCode + code, capacity)
        )
    }

    return (
        <div className={'panel-card location-view'}>
            <div className='d-flex justify-content-between align-items-center'>
                <div className={'d-flex align-items-center'}>
                    <button className='button primary icon-button back'
                            onClick={navigateBack}>
                        <BackIcon/>
                    </button>
                    <p className='title'>
                        {translations?.rows?.title ?? ''}
                    </p>
                </div>
                <div>
                    <button className='button outlined primary report'
                            onClick={showGenerateReportDialog}>
                        <GenerateReportIcon className='me-2'/>
                        {
                            generatingReport
                                ? translations?.actions?.generateReport?.loading ?? ''
                                : translations?.actions?.generateReport?.title ?? ''
                        }
                    </button>
                </div>
            </div>
            <div className={'mt-2'}>
                <BreadCrumbs data={breadcrumbs}/>
            </div>
            <div className='mt-4'>
                {
                    loading
                        ? (
                            <div className='d-flex flex-wrap'>
                                {
                                    Array(10).fill(null).map((e, index) => (
                                        <div key={index} className={'loading location-card'}>
                                            <div/>
                                        </div>
                                    ))
                                }
                            </div>
                        )
                        : rows?.length < 1
                            ? <NoEntries
                                text={translations?.rows?.noEntries ?? ''}
                                includeButton
                                buttonText={translations?.rows?.create}
                                buttonProps={{
                                    className: 'button primary px-4',
                                    disabled: generatingReport,
                                    onClick: showAddRowDialog
                                }}
                            />
                            : <TransitionGroup className='d-flex flex-wrap'>
                                {
                                    rows?.map(location => (
                                            <CSSTransition
                                                classNames={'fade'}
                                                timeout={{enter: 800, exit: 300,}}
                                                key={location.row.id}>
                                                <LocationCard
                                                    location={location.row}
                                                    onEdit={showEditRowDialog}
                                                    onRemove={removeRow}
                                                    type={LocationTypes.row}
                                                />
                                            </CSSTransition>
                                        )
                                    )
                                }
                                <CSSTransition
                                    key={'new-location-entry'}
                                    classNames={'fade'}
                                    timeout={{enter: 800, exit: 300,}}>
                                    <button
                                        className={'button primary add-location'}
                                        onClick={showAddRowDialog}>
                                        <p>
                                            {translations?.actions?.addNew ?? ''}
                                        </p>
                                        <div className='mt-2 text-center'>
                                            <PlusIcon/>
                                        </div>
                                    </button>
                                </CSSTransition>
                            </TransitionGroup>
                }
            </div>
        </div>
    )
};

export default RowsView;
